import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import groupBy from "lodash/groupBy";
import type { CarConfigStateType } from "../store";
import type { AtmosphereType } from "../../utils/types/AtmosphereType";
import type { FinanceCarItemType } from "../../../../../common-deprecated/features/apheleia/utils/types/Finance";
import type { AbortableRequestStatus } from "../../../../../common-deprecated/types/CommonTypes";
import { RequestStatusEnum } from "../../../../../common-deprecated/types/CommonTypes";

export type CarConfigAtmosphereFinanceType = {
    state: AbortableRequestStatus;
    cars: Record<string, FinanceCarItemType>;
};

export type CarConfigAtmosphereType = {
    initialized: boolean;
    atmospheres: AtmosphereType[];
    finance: CarConfigAtmosphereFinanceType;
};

export const initialStateCarConfigAtmosphere: CarConfigAtmosphereType = {
    initialized: false,
    atmospheres: [],
    finance: {
        state: RequestStatusEnum.Idle,
        cars: {},
    },
};

export type CarConfigAtmosphereActionTypes = ReturnType<
    | typeof initCarConfigAtmospheres
    | typeof initCarConfigFinanceAtmospheres
    | typeof setCarConfigFinanceAtmospheresState
>;

export const CarConfigAtmosphereSlice = createSlice({
    name: "carConfigAtmosphere",
    initialState: initialStateCarConfigAtmosphere,
    reducers: {
        initCarConfigAtmospheres(state, action: PayloadAction<{ atmospheres: AtmosphereType[] }>) {
            return {
                ...state,
                ...action.payload,
                initialized: true,
            };
        },
        initCarConfigFinanceAtmospheres(state, action: PayloadAction<CarConfigAtmosphereFinanceType["cars"]>) {
            return {
                ...state,
                finance: {
                    ...state.finance,
                    state: RequestStatusEnum.Complete,
                    cars: action.payload,
                },
            };
        },
        setCarConfigFinanceAtmospheresState(state, action: PayloadAction<CarConfigAtmosphereFinanceType["state"]>) {
            return {
                ...state,
                finance: {
                    ...state.finance,
                    state: action.payload,
                },
            };
        },
    },
});

export const { initCarConfigAtmospheres, initCarConfigFinanceAtmospheres, setCarConfigFinanceAtmospheresState } =
    CarConfigAtmosphereSlice.actions;

export const selectCurrentAtmosphere = (state: CarConfigStateType): AtmosphereType | undefined =>
    state.carConfigAtmosphere.atmospheres.find((atmosphere) => atmosphere.id === state.car.grade.id);

/**
 * Returns the index of the current selected atmosphere's category.
 * e.g. if an atmosphere is currently selected of atmosphere category "base", it will return index 0.
 */
export const selectCurrentAtmosphereGroupIndex = (state: CarConfigStateType): number => {
    return Object.entries(groupBy(state.carConfigAtmosphere.atmospheres, "category")).findIndex(([, atmosphereGroup]) =>
        atmosphereGroup.find((atmosphere) => atmosphere.category === selectCurrentAtmosphere(state)?.category),
    );
};
